import { useState, useEffect } from 'react';
import type { AnnualLicenseConfig } from '@/mocks/superAdminPaymentProcessors';

interface Props {
  open: boolean;
  onClose: () => void;
  config: AnnualLicenseConfig | null;
  onSave: (config: AnnualLicenseConfig) => void;
}

export default function AnnualLicenseEditorModal({ open, onClose, config, onSave }: Props) {
  const [draft, setDraft] = useState<AnnualLicenseConfig | null>(null);
  const [newItem, setNewItem] = useState('');

  useEffect(() => {
    if (config) {
      setDraft({ ...config, includes: [...config.includes], stats: { ...config.stats } });
    }
    setNewItem('');
  }, [config, open]);

  if (!open || !draft) return null;

  const handleAddItem = () => {
    const trimmed = newItem.trim();
    if (!trimmed) return;
    if (draft.includes.some((i) => i.toLowerCase() === trimmed.toLowerCase())) return;
    setDraft((prev) => prev ? { ...prev, includes: [...prev.includes, trimmed] } : null);
    setNewItem('');
  };

  const handleRemoveItem = (idx: number) => {
    setDraft((prev) => {
      if (!prev) return null;
      const next = [...prev.includes];
      next.splice(idx, 1);
      return { ...prev, includes: next };
    });
  };

  const handleSave = () => {
    if (!draft) return;
    onSave(draft);
    onClose();
  };

  const projected = draft.stats.payingRadios * draft.amount;
  const formattedProjected = projected.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh]">
      {/* Backdrop */}
      <button className="fixed inset-0 bg-black/45 backdrop-blur-sm cursor-default" onClick={onClose}></button>

      {/* Panel */}
      <div className="relative w-full max-w-lg bg-background-50 rounded-2xl border border-background-200 shadow-2xl mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-100 shrink-0">
          <div>
            <h3 className="text-foreground-950 font-bold text-sm">Editar Licencia Anual</h3>
            <p className="text-foreground-400 text-[10px] mt-0.5">Configuración global de la plataforma</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer hover:bg-background-100 transition-colors text-foreground-400"
          >
            <i className="ri-close-line text-sm"></i>
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-5 py-4 space-y-5 flex-1">
          {/* Estado */}
          <div>
            <label className="text-foreground-800 text-[11px] font-semibold block mb-2">Estado</label>
            <div className="inline-flex rounded-xl bg-background-100 p-1">
              <button
                onClick={() => setDraft((prev) => prev ? { ...prev, enabled: true } : null)}
                className={`px-4 py-2 rounded-lg text-xs font-bold transition-colors cursor-pointer whitespace-nowrap ${draft.enabled ? 'bg-emerald-500 text-white' : 'text-foreground-400 hover:text-foreground-600'}`}
              >
                <i className={`${draft.enabled ? 'ri-check-line' : 'ri-check-line'} mr-1 text-[11px]`}></i>
                Activa
              </button>
              <button
                onClick={() => setDraft((prev) => prev ? { ...prev, enabled: false } : null)}
                className={`px-4 py-2 rounded-lg text-xs font-bold transition-colors cursor-pointer whitespace-nowrap ${!draft.enabled ? 'bg-foreground-800 text-white' : 'text-foreground-400 hover:text-foreground-600'}`}
              >
                <i className={`${!draft.enabled ? 'ri-close-line' : 'ri-close-line'} mr-1 text-[11px]`}></i>
                Desactivada
              </button>
            </div>
          </div>

          {/* Monto */}
          <div>
            <label className="text-foreground-800 text-[11px] font-semibold block mb-2">Monto (USD)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm font-bold">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={draft.amount}
                onChange={(e) => setDraft((prev) => prev ? { ...prev, amount: parseFloat(e.target.value) || 0 } : null)}
                className="w-full pl-7 pr-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-foreground-800 text-sm font-bold focus:outline-none focus:border-foreground-300 transition-colors"
                placeholder="49.95"
              />
            </div>
            <p className="text-foreground-400 text-[9px] mt-1">Periodicidad: <strong className="text-foreground-600">Anual</strong></p>
          </div>

          {/* Descripción */}
          <div>
            <label className="text-foreground-800 text-[11px] font-semibold block mb-2">Descripción</label>
            <input
              type="text"
              value={draft.description}
              onChange={(e) => setDraft((prev) => prev ? { ...prev, description: e.target.value } : null)}
              className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-foreground-800 text-sm focus:outline-none focus:border-foreground-300 transition-colors"
              placeholder="Licencia anual de acceso a RadioFrecuenciaWeb."
            />
          </div>

          {/* Incluye */}
          <div>
            <label className="text-foreground-800 text-[11px] font-semibold block mb-2">Incluye</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newItem}
                onChange={(e) => setNewItem(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddItem(); } }}
                className="flex-1 px-4 py-2 rounded-xl bg-background-100 border border-background-200 text-foreground-800 text-sm focus:outline-none focus:border-foreground-300 transition-colors"
                placeholder="Agregar item..."
              />
              <button
                onClick={handleAddItem}
                className="w-10 h-10 rounded-xl bg-foreground-950 text-background-50 flex items-center justify-center cursor-pointer hover:bg-foreground-800 transition-colors shrink-0"
              >
                <i className="ri-add-line text-sm"></i>
              </button>
            </div>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {draft.includes.length === 0 && (
                <p className="text-foreground-300 text-[10px] italic py-2">Sin items. Agregá los beneficios incluidos.</p>
              )}
              {draft.includes.map((item, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background-100 border border-background-100 hover:border-background-200 transition-colors group">
                  <i className="ri-check-line text-emerald-500 text-xs shrink-0"></i>
                  <span className="text-foreground-700 text-xs flex-1 truncate">{item}</span>
                  <button
                    onClick={() => handleRemoveItem(i)}
                    className="w-6 h-6 rounded-md flex items-center justify-center cursor-pointer text-foreground-300 hover:text-red-400 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100 shrink-0"
                  >
                    <i className="ri-close-line text-[11px]"></i>
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Stats preview */}
          <div className="bg-background-100 rounded-xl p-4 border border-background-200">
            <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-3">Proyección</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-foreground-400 text-[9px]">Radios que pagan</p>
                <p className="text-foreground-800 text-sm font-bold">{draft.stats.payingRadios.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-foreground-400 text-[9px]">Vencen este mes</p>
                <p className="text-foreground-800 text-sm font-bold">{draft.stats.expiringThisMonth.toLocaleString()}</p>
              </div>
              <div className="col-span-2">
                <p className="text-foreground-400 text-[9px]">Ingresos proyectados</p>
                <p className="text-foreground-800 text-base font-bold">${formattedProjected}</p>
                <p className="text-foreground-400 text-[9px] mt-0.5">{draft.stats.payingRadios.toLocaleString()} × ${draft.amount.toFixed(2)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-background-100 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl bg-background-100 text-foreground-600 text-xs font-bold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-save-line mr-1.5 text-[11px]"></i>Guardar cambios
          </button>
        </div>
      </div>
    </div>
  );
}